import React, { useState, useEffect, useMemo } from "react";
import {
  Stack,
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
  Drawer,
  IconButton,
  Divider,
  Button,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import LaserMarkPanel from "../../components/LaserMarkPanel";
import MarkingTimeChip from "../../components/MarkingTimeChip";
import { downloadBlob, getFilenameFromContentDisposition } from "../../helper/markingPreviewWindow";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import axios from "axios";
import CaptureImage from "../../components/CaptureImage";
// A patch is painted with its measured L*a*b* (labCss), never the camera RGB;
// null when unmeasured, so the caller falls back to a neutral fill.
import { labCss } from "../../helper/colorConversion";

// Single neutral color used for every occupied slot and iteration chip; the
// iteration / batch label inside the cell is enough to disambiguate them.
const OCCUPIED_COLOR = "#546e7a";
const OCCUPIED_BORDER = "rgba(0,0,0,0.18)";

// Build the stacked label lines for a slot, e.g. ``P0`` over ``B0`` over
// ``cluster`` or ``Q3`` over ``B1`` over ``color``. ``phase`` is "P" for the
// initial input population (only iteration 0 has one) and "Q" for every
// generated population that gets marked at the end of an iteration; the
// pass-kind line distinguishes the SINGLE_CLUSTER (cluster, width-measurement)
// and FULL_CLUSTER (color, color-measurement) slots that the same patch group
// occupies side-by-side on the plate. We fall back to ``i`` when the backend
// could not classify the patch group's collection (older data).
function slotLabelLines(cell) {
  if (!cell) return null;
  const iter = cell.iteration_number ?? "?";
  const batch = cell.batch_number ?? "?";
  const phase = cell.phase || "i";
  const passKind = (cell.pass_kind || "").toLowerCase();
  let passLine = "";
  if (passKind === "cluster") passLine = "cluster";
  else if (passKind === "color") passLine = "color";
  return {
    iterationLine: `${phase}${iter}`,
    batchLine: `B${batch}`,
    passLine,
  };
}

// Plate sizes come as floats (85, 55, 0.8); show up to one decimal.
const formatMm = (value) =>
  Number.isFinite(value) ? String(Math.round(value * 10) / 10) : "?";

// Where the plate sits under the laser (its top-left corner relative to the
// marking field origin, chosen at setup) and what part of it the field covers.
function placementHint(offset, usable) {
  if (!offset) return "";
  let hint = ` at x ${formatMm(offset.x)}, y ${formatMm(offset.y)} mm`;
  if (usable) hint += ` • usable ${formatMm(usable.width)} × ${formatMm(usable.height)} mm`;
  return hint;
}

function phaseDescription(phase) {
  if (phase === "P") return "Initial input population";
  if (phase === "Q") return "Generated population";
  return "Unclassified";
}

function passKindDescription(passKind) {
  if (passKind === "CLUSTER") return "Cluster pass (width measurement)";
  if (passKind === "COLOR") return "Color pass (color measurement)";
  return null;
}

function SlotCell({ cell, onSelect, selected = false }) {
  const isOccupied = !!cell;
  const bg = isOccupied ? OCCUPIED_COLOR : "#f5f5f5";
  const fg = isOccupied ? "#fff" : "#9e9e9e";
  const passDescription = isOccupied ? passKindDescription(cell.pass_kind) : null;
  const tooltipBody = isOccupied
    ? `${phaseDescription(cell.phase)} • Iteration ${cell.iteration_number ?? "?"} • Batch ${cell.batch_number}\n` +
      (passDescription ? `${passDescription}\n` : "") +
      `Type ${cell.type} • Status ${cell.status}\n` +
      `Slot ${cell.slot_index}\n` +
      `Click to inspect patches`
    : "Free slot";
  const labelLines = slotLabelLines(cell);
  const clickable = isOccupied && typeof onSelect === "function";

  return (
    <Tooltip title={<Box sx={{ whiteSpace: "pre-line" }}>{tooltipBody}</Box>} arrow>
      <Box
        onClick={clickable ? () => onSelect(cell) : undefined}
        sx={{
          aspectRatio: "1 / 1",
          borderRadius: 1,
          border: "1px solid",
          borderColor: isOccupied ? OCCUPIED_BORDER : "rgba(0,0,0,0.08)",
          bgcolor: bg,
          color: fg,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: 600,
          lineHeight: 1.15,
          userSelect: "none",
          cursor: clickable ? "pointer" : "default",
          transition: "transform 0.06s ease, box-shadow 0.06s ease",
          "&:hover": clickable
            ? { transform: "translateY(-1px)", boxShadow: 2 }
            : undefined,
          ...(selected
            ? { outline: "3px solid #3B6AE6", outlineOffset: "2px", boxShadow: 3 }
            : {}),
        }}
      >
        {labelLines ? (
          <>
            <Box component="span">{labelLines.iterationLine}</Box>
            <Box component="span">{labelLines.batchLine}</Box>
            {labelLines.passLine ? (
              <Box
                component="span"
                sx={{
                  fontSize: 9,
                  fontWeight: 500,
                  fontStyle: "italic",
                  opacity: 0.85,
                  mt: 0.25,
                }}
              >
                {labelLines.passLine}
              </Box>
            ) : null}
          </>
        ) : null}
      </Box>
    </Tooltip>
  );
}

// ``isSlotSelected(cell)`` outlines a slot (the tile opened next to the
// grid); ``maxWidth`` caps the grid for narrower layouts.
function PlateGrid({ plate, slots, onSelectSlot, isSlotSelected, maxWidth = 720 }) {
  const cntY = plate?.cnt_y || 0;
  const cntX = plate?.cnt_x || 0;

  const slotsByIndex = useMemo(() => {
    const map = new Map();
    for (const s of slots) {
      if (typeof s.slot_index === "number") {
        map.set(s.slot_index, s);
      }
    }
    return map;
  }, [slots]);

  if (!plate) return null;
  if (!cntY || !cntX) {
    return (
      <Alert severity="info">
        Plate layout is not initialized yet. Run an iteration to populate it.
      </Alert>
    );
  }

  const cells = [];
  for (let row = 0; row < cntY; row += 1) {
    for (let col = 0; col < cntX; col += 1) {
      const idx = row * cntX + col;
      cells.push(slotsByIndex.get(idx) || null);
    }
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${cntX}, minmax(0, 1fr))`,
        gridAutoRows: "minmax(0, 1fr)",
        gap: 0.75,
        width: "100%",
        maxWidth,
      }}
    >
      {cells.map((cell, i) => (
        <SlotCell
          key={i}
          cell={cell}
          onSelect={onSelectSlot}
          selected={Boolean(cell && typeof isSlotSelected === "function" && isSlotSelected(cell))}
        />
      ))}
    </Box>
  );
}

// Compact numeric formatting for the hover popover: integers stay integers,
// floats are trimmed to a few significant decimals, null/undefined -> dash.
function formatNum(v) {
  if (v === null || v === undefined || (typeof v === "number" && Number.isNaN(v))) {
    return "–";
  }
  if (typeof v === "number") {
    if (Number.isInteger(v)) return String(v);
    return v
      .toFixed(3)
      .replace(/0+$/, "")
      .replace(/\.$/, "");
  }
  return String(v);
}

// Rich tooltip body for a single patch: ID(s), design-space parameters, and
// measured performance (color / width, whichever exists).
//
// ``heading`` replaces the default "Row · Col" title and ``lines`` are extra
// label/value rows shown under it (the analysis picker uses them for the
// member's rank and where it was marked).
function PatchTooltip({ patch, heading, lines }) {
  const design = patch.design || {};
  const perf = patch.performance || {};
  const designKeys = Object.keys(design).sort();
  const hasColor = ["r", "g", "b", "l_star"].some(
    (k) => perf[k] !== null && perf[k] !== undefined
  );
  const hasWidth = perf.width_mm !== null && perf.width_mm !== undefined;

  const Row = ({ label, value }) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
      <Box component="span" sx={{ opacity: 0.7 }}>
        {label}
      </Box>
      <Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ fontSize: 11, lineHeight: 1.5, minWidth: 200 }}>
      <Box sx={{ fontWeight: 700, mb: 0.5 }}>
        {heading || `Row ${patch.row} · Col ${patch.column}`}
      </Box>
      {(lines || []).map((line) => (
        <Row key={line.label} label={line.label} value={line.value} />
      ))}

      <Box sx={{ fontWeight: 600, opacity: 0.85, mt: 0.5 }}>Design</Box>
      {designKeys.length ? (
        designKeys.map((k) => <Row key={k} label={k} value={formatNum(design[k])} />)
      ) : (
        <Box sx={{ opacity: 0.6 }}>No design parameters</Box>
      )}

      <Box sx={{ fontWeight: 600, opacity: 0.85, mt: 0.75 }}>Performance</Box>
      {hasWidth ? <Row label="width (mm)" value={formatNum(perf.width_mm)} /> : null}
      {hasColor ? (
        <>
          <Row
            label="RGB"
            value={`${formatNum(perf.r)}, ${formatNum(perf.g)}, ${formatNum(perf.b)}`}
          />
          <Row
            label="L*a*b*"
            value={`${formatNum(perf.l_star)}, ${formatNum(perf.a_star)}, ${formatNum(
              perf.b_star
            )}`}
          />
        </>
      ) : null}
      {!hasWidth && !hasColor ? (
        <Box sx={{ opacity: 0.6 }}>Not measured yet</Box>
      ) : null}

      <Divider sx={{ my: 0.75, borderColor: "rgba(255,255,255,0.25)" }} />
      <Box sx={{ fontFamily: "monospace", fontSize: 9.5, opacity: 0.8, wordBreak: "break-all" }}>
        {`sample ${patch.sample_id}`}
      </Box>
      <Box sx={{ fontFamily: "monospace", fontSize: 9.5, opacity: 0.8, wordBreak: "break-all" }}>
        {`patch ${patch.patch_id}`}
      </Box>
    </Box>
  );
}

// The grid of individual patches inside one tile, laid out by the stored
// (row, column) so screen position matches the physical plate. Color-pass
// patches are tinted with their measured RGB.
//
// Optional picker mode (used by the analysis page to choose a sample by
// clicking its patch instead of scrolling a dropdown):
//   onSelectPatch(patch)      called with the clicked patch
//   selectedSampleId          the patch whose sample matches is outlined
//   isPatchSelectable(patch)  patches for which this returns false are dimmed
//                             and not clickable (e.g. members of a marking
//                             that do not belong to the collection browsed)
//   isPatchPicked(patch)      patches for which this returns true carry a
//                             check mark (several at once: the manual
//                             selection's palette)
//   patchTooltipLines(patch)  extra label/value rows for the hover card
//   gap                       MUI spacing between cells (default 0.5)
function PatchGrid({
  data,
  passKind,
  onSelectPatch,
  selectedSampleId,
  isPatchSelectable,
  isPatchPicked,
  patchTooltipLines,
  gap = 0.5,
}) {
  const rows = data?.rows || 0;
  const columns = data?.columns || 0;
  const byPos = useMemo(() => {
    const map = new Map();
    for (const p of data?.patches || []) {
      map.set(`${p.row}-${p.column}`, p);
    }
    return map;
  }, [data]);

  if (!rows || !columns) {
    return <Alert severity="info">No patches found for this tile.</Alert>;
  }
  const pickerMode = typeof onSelectPatch === "function";
  const wantedSampleId = selectedSampleId ? String(selectedSampleId) : "";

  const cells = [];
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < columns; c += 1) {
      cells.push(byPos.get(`${r}-${c}`) || null);
    }
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gridAutoRows: "minmax(0, 1fr)",
        gap,
        width: "100%",
      }}
    >
      {cells.map((patch, i) => {
        if (!patch) {
          return (
            <Box
              key={i}
              sx={{
                aspectRatio: "1 / 1",
                borderRadius: 0.5,
                border: "1px dashed rgba(0,0,0,0.12)",
                bgcolor: "#fafafa",
              }}
            />
          );
        }
        const tint = passKind === "COLOR" ? labCss(patch.performance) : null;
        const selectable = pickerMode
          ? (typeof isPatchSelectable === "function" ? isPatchSelectable(patch) : true)
          : true;
        const isSelected = pickerMode && wantedSampleId && String(patch.sample_id) === wantedSampleId;
        const isPicked = typeof isPatchPicked === "function" && isPatchPicked(patch);
        const extraLines = typeof patchTooltipLines === "function" ? patchTooltipLines(patch) : undefined;
        return (
          <Tooltip key={i} arrow placement="top" title={<PatchTooltip patch={patch} lines={extraLines} />}>
            <Box
              onClick={pickerMode && selectable ? () => onSelectPatch(patch) : undefined}
              data-picked={isPicked ? "true" : undefined}
              data-sample-id={patch.sample_id}
              role={pickerMode && selectable ? "button" : undefined}
              aria-label={pickerMode ? `Sample ${patch.sample_id}${isPicked ? " (picked)" : ""}` : undefined}
              sx={{
                position: "relative",
                aspectRatio: "1 / 1",
                borderRadius: 0.5,
                border: "1px solid rgba(0,0,0,0.18)",
                bgcolor: tint || OCCUPIED_COLOR,
                cursor: selectable ? "pointer" : "default",
                opacity: selectable ? 1 : 0.25,
                transition: "transform 0.06s ease, box-shadow 0.06s ease",
                "&:hover": selectable
                  ? { transform: "scale(1.06)", boxShadow: 3, zIndex: 1 }
                  : undefined,
                ...(isPicked
                  ? { outline: "3px solid #16a34a", outlineOffset: "1px", zIndex: 1 }
                  : {}),
                ...(isSelected
                  ? {
                      outline: "3px solid #3B6AE6",
                      outlineOffset: "1px",
                      boxShadow: "0 0 0 5px rgba(255,255,255,0.9)",
                      zIndex: 2,
                    }
                  : {}),
              }}
            >
              {isPicked ? (
                <Box
                  component="span"
                  aria-hidden
                  sx={{
                    position: "absolute",
                    top: 3,
                    right: 3,
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    bgcolor: "#16a34a",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 0 0 2px rgba(255,255,255,0.95)",
                  }}
                >
                  <CheckIcon sx={{ fontSize: 14 }} />
                </Box>
              ) : null}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}

// Side drawer that loads and shows the patch grid for one clicked tile. Owns
// its own fetch keyed by the selected slot so the parent view stays simple.
function PatchGroupDrawer({ explorationId, plateId, slot, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!explorationId || !plateId || !slot) {
      setData(null);
      return undefined;
    }
    setLoading(true);
    setError("");
    setData(null);
    const passKind = slot.pass_kind || "CLUSTER";
    axios
      .get(
        `api/exploration/${explorationId}/plates/${plateId}/patch-group/${slot.patch_group_id}/patches/`,
        { params: { pass_kind: passKind } }
      )
      .then((response) => {
        if (cancelled) return;
        setData(response.data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err?.response?.data?.message ||
            err.message ||
            "Failed to load patches for this tile."
        );
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [explorationId, plateId, slot]);

  const open = !!slot;
  const labelLines = slotLabelLines(slot);
  const passKind = slot?.pass_kind || "CLUSTER";

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: 320, sm: 420 }, p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {labelLines
                ? `${labelLines.iterationLine} · ${labelLines.batchLine}`
                : "Patch group"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {passKindDescription(passKind) || "Patches"}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>

        {slot ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            {`Slot ${slot.slot_index} • ${phaseDescription(slot.phase)} • Status ${slot.status}`}
          </Typography>
        ) : null}

        <Divider sx={{ my: 2 }} />

        {error ? <Alert severity="error">{error}</Alert> : null}

        {loading ? (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CircularProgress size={20} />
            <Typography>Loading patches...</Typography>
          </Stack>
        ) : data ? (
          <>
            <Box sx={{ mb: 2 }}>
              <CaptureImage
                explorationId={explorationId}
                plateId={plateId}
                patchGroupId={slot?.patch_group_id}
                passKind={passKind}
                sourceImage={data.source_image}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {`${data.rows} × ${data.columns} patches — hover a patch for its design parameters, performance and ID.`}
            </Typography>
            <PatchGrid data={data} passKind={passKind} />
          </>
        ) : null}
      </Box>
    </Drawer>
  );
}

/**
 * Plates view body — plate selector, plate metadata, slot occupancy grid, and
 * the "Download plate label" button. This is the view the user opens via the
 * "Plates" tab on the Analyse page (no surrounding shell / back button), so it
 * can be embedded directly inside another tabbed page without owning the
 * exploration-id resolution.
 *
 * Props:
 *   explorationId: string  the exploration whose plates to render
 */
/** The plate header as a marking file of its own (marked separately, e.g. on the
 * back of a small plate whose front keeps the header band free). */
async function fetchHeaderMarkingFile(explorationId, plate) {
  const response = await axios.get(
    `api/exploration/${explorationId}/plates/${plate.id}/header-marking-file/`,
    { responseType: "blob" }
  );
  const filename =
    getFilenameFromContentDisposition(response.headers?.["content-disposition"]) ||
    `header_marking_file_plate${plate.plate_number}.json`;
  return { blob: response.data, filename };
}

/** The predicted marking time of the plate header file (timeOnly builds no file). */
async function fetchHeaderMarkingTime(explorationId, plate) {
  const response = await axios.get(
    `api/exploration/${explorationId}/plates/${plate.id}/header-marking-file/?timeOnly=1`
  );
  return response.data?.marking_time || null;
}

function PlatesView({ explorationId: explorationIdProp }) {
  const explorationId = explorationIdProp || "";

  const [plates, setPlates] = useState([]);
  // Physical plate (the exploration's substrate) every slot grid is laid
  // out on, and the laser's marking field: { width, height } in mm each,
  // or null on older API responses.
  const [plateSizeMm, setPlateSizeMm] = useState(null);
  const [plateOffsetMm, setPlateOffsetMm] = useState(null);
  const [usableMm, setUsableMm] = useState(null);
  const [selectedPlateId, setSelectedPlateId] = useState("");
  const [occupancy, setOccupancy] = useState(null);
  const [loadingPlates, setLoadingPlates] = useState(false);
  const [loadingOccupancy, setLoadingOccupancy] = useState(false);
  const [error, setError] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [headerDownloading, setHeaderDownloading] = useState(false);
  const [headerError, setHeaderError] = useState("");
  const [headerMarkingTime, setHeaderMarkingTime] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!explorationId) {
      setError("No exploration selected.");
      return undefined;
    }
    setLoadingPlates(true);
    setError("");
    axios
      .get(`api/exploration/${explorationId}/plates/`)
      .then((response) => {
        if (cancelled) return;
        const incoming = response.data?.plates || [];
        setPlates(incoming);
        setPlateSizeMm(response.data?.plate_size_mm || null);
        setPlateOffsetMm(response.data?.plate_offset_mm || null);
        setUsableMm(response.data?.usable_mm || null);
        setSelectedPlateId((prev) => {
          if (prev && incoming.some((p) => p.id === prev)) return prev;
          return incoming.length > 0 ? incoming[0].id : "";
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err?.response?.data?.message ||
            err.message ||
            "Failed to load plates for this exploration."
        );
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingPlates(false);
      });
    return () => {
      cancelled = true;
    };
  }, [explorationId]);

  useEffect(() => {
    let cancelled = false;
    setSelectedSlot(null);
    if (!explorationId || !selectedPlateId) {
      setOccupancy(null);
      return undefined;
    }
    setLoadingOccupancy(true);
    axios
      .get(
        `api/exploration/${explorationId}/plates/${selectedPlateId}/occupancy/`
      )
      .then((response) => {
        if (cancelled) return;
        setOccupancy(response.data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err?.response?.data?.message ||
            err.message ||
            "Failed to load plate occupancy."
        );
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingOccupancy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [explorationId, selectedPlateId]);

  const selectedPlate = useMemo(
    () => plates.find((p) => p.id === selectedPlateId) || occupancy?.plate || null,
    [plates, selectedPlateId, occupancy]
  );

  // The header file's predicted marking time follows the selected plate.
  useEffect(() => {
    if (!explorationId || !selectedPlate?.id) {
      setHeaderMarkingTime(null);
      return undefined;
    }
    let cancelled = false;
    fetchHeaderMarkingTime(explorationId, selectedPlate)
      .then((value) => { if (!cancelled) setHeaderMarkingTime(value); })
      .catch(() => { if (!cancelled) setHeaderMarkingTime(null); });
    return () => { cancelled = true; };
  }, [explorationId, selectedPlate?.id]);

  const slots = occupancy?.slots || [];

  return (
    <Stack spacing={3}>
      {error ? <Alert severity="error">{error}</Alert> : null}

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
      >
        <FormControl size="small" sx={{ minWidth: 240 }} disabled={loadingPlates}>
          <InputLabel id="plate-selector-label">Plate</InputLabel>
          <Select
            labelId="plate-selector-label"
            label="Plate"
            value={selectedPlateId}
            onChange={(e) => setSelectedPlateId(e.target.value)}
          >
            {plates.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name || `Plate ${p.plate_number}`}
                {typeof p.occupied_slots === "number" &&
                typeof p.slots_total === "number"
                  ? ` (${p.occupied_slots}/${p.slots_total} used)`
                  : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {loadingPlates ? (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={20} />
          <Typography>Loading plates...</Typography>
        </Stack>
      ) : plates.length === 0 ? (
        <Alert severity="info">
          No plates have been allocated yet for this exploration. Plates are
          created automatically the first time an iteration's batches are
          marked.
        </Alert>
      ) : null}

      {selectedPlate ? (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            bgcolor: "#fff",
            borderColor: "#dde1e7",
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {selectedPlate.name || `Plate ${selectedPlate.plate_number}`}
            </Typography>
            {selectedPlate.id ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  fontFamily:
                    "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                  wordBreak: "break-all",
                }}
              >
                {`ID: ${selectedPlate.id}`}
              </Typography>
            ) : null}
            {plateSizeMm ? (
              <Typography variant="body2" color="text.secondary">
                {`${formatMm(plateSizeMm.width)} × ${formatMm(plateSizeMm.height)} mm plate`}
                {placementHint(plateOffsetMm, usableMm)}
              </Typography>
            ) : null}
            <Typography variant="body2" color="text.secondary">
              {`${selectedPlate.cnt_y ?? "?"} × ${selectedPlate.cnt_x ?? "?"} grid`}
              {typeof selectedPlate.slots_total === "number"
                ? ` • ${selectedPlate.slots_total} slots`
                : ""}
              {typeof selectedPlate.occupied_slots === "number"
                ? ` • ${selectedPlate.occupied_slots} occupied`
                : ""}
            </Typography>
            <Stack spacing={1} sx={{ mt: 1.5, alignItems: "flex-start" }}>
              <Button
                variant="outlined"
                size="small"
                disableElevation
                startIcon={headerDownloading ? <CircularProgress size={14} /> : <DownloadIcon />}
                disabled={headerDownloading}
                sx={{ textTransform: "none" }}
                onClick={async () => {
                  setHeaderDownloading(true);
                  setHeaderError("");
                  try {
                    const { blob, filename } = await fetchHeaderMarkingFile(explorationId, selectedPlate);
                    downloadBlob(blob, filename);
                  } catch (err) {
                    setHeaderError(
                      err?.response?.data?.error || err.message || "The header marking file could not be built."
                    );
                  } finally {
                    setHeaderDownloading(false);
                  }
                }}
              >
                Header marking file
              </Button>
              <MarkingTimeChip value={headerMarkingTime} />
              {headerError ? <Alert severity="error">{headerError}</Alert> : null}
              <LaserMarkPanel
                getMarkingBlob={() => fetchHeaderMarkingFile(explorationId, selectedPlate)}
                predictedMarkingTime={headerMarkingTime}
                markingContext={{ explorationId, iterationNumber: null, patchType: "PLATE_HEADER" }}
              >
                <Typography variant="body2" color="text.secondary">
                  Mark the plate header on the laser connected to this computer:
                </Typography>
              </LaserMarkPanel>
            </Stack>
          </Box>
          {loadingOccupancy ? (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={20} />
              <Typography>Loading occupancy...</Typography>
            </Stack>
          ) : (
            <PlateGrid
              plate={selectedPlate}
              slots={slots}
              onSelectSlot={setSelectedSlot}
            />
          )}
        </Paper>
      ) : null}

      <PatchGroupDrawer
        explorationId={explorationId}
        plateId={selectedPlateId}
        slot={selectedSlot}
        onClose={() => setSelectedSlot(null)}
      />
    </Stack>
  );
}

export {
  PlatesView,
  PlateGrid,
  PatchGrid,
  PatchTooltip,
  labCss,
  slotLabelLines,
  passKindDescription,
  OCCUPIED_COLOR,
};
export default PlatesView;
